import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, BookOpen, Volume2, FileText, Download, Play, 
  Pause, CloudOff, Search, Clock, Loader2, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { offlineStorage } from '@/services/offlineStorage';
import { OposaiLogo } from '@/components/TuPlazaIALogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LibraryPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('books');
  const [library, setLibrary] = useState({ audios: [], pdfs: [], materials: [], books: [] });
  const [searchTerm, setSearchTerm] = useState('');
  const [downloadingOffline, setDownloadingOffline] = useState(false);
  const [offlineStats, setOfflineStats] = useState({ audios: 0, pdfs: 0 });
  
  // Audio player state
  const [currentAudio, setCurrentAudio] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioRef, setAudioRef] = useState(null);

  useEffect(() => {
    loadLibrary();
    loadOfflineStats();
  }, []);

  const loadLibrary = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/library`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Get audios from audios endpoint
      const audiosRes = await axios.get(`${API}/audios`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: { audios: [] } }));
      
      // Get generated books
      const booksRes = await axios.get(`${API}/books/list`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: { books: [] } }));
      
      setLibrary({
        audios: audiosRes.data?.audios || response.data?.audios || [],
        pdfs: response.data?.materials || [],
        materials: response.data?.books || [],
        books: booksRes.data?.books || []
      });
    } catch (error) {
      console.error('Error loading library:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOfflineStats = async () => {
    try {
      const stats = await offlineStorage.getStats();
      setOfflineStats({
        audios: stats.audioCount || 0,
        pdfs: 0 // PDFs stored separately
      });
    } catch (e) {
      console.log('Error loading offline stats');
    }
  };

  const downloadForOffline = async () => {
    setDownloadingOffline(true);
    try {
      const token = localStorage.getItem('session_token');
      
      // Download library package
      const response = await axios.get(`${API}/library/offline-package`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Save audios to IndexedDB
      if (response.data?.audios?.length) {
        await offlineStorage.saveAudios(response.data.audios);
      }
      
      toast.success(`¡Biblioteca descargada! ${response.data?.totals?.audios || 0} audios, ${response.data?.totals?.pdfs || 0} documentos`);
      await loadOfflineStats();
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Error al descargar biblioteca');
    } finally {
      setDownloadingOffline(false);
    }
  };

  const playAudio = (audio) => {
    if (currentAudio?.audio_id === audio.audio_id && isPlaying) {
      // Pause current audio
      audioRef?.pause();
      setIsPlaying(false);
    } else {
      // Play new audio
      if (audioRef) {
        audioRef.pause();
      }
      
      const audioUrl = audio.audio_url || audio.url;
      if (!audioUrl) {
        toast.error('Audio no disponible');
        return;
      }
      
      const newAudio = new Audio(audioUrl);
      newAudio.play().catch(e => {
        toast.error('Error al reproducir audio');
        console.error(e);
      });
      
      newAudio.onended = () => {
        setIsPlaying(false);
        setCurrentAudio(null);
      };
      
      setAudioRef(newAudio);
      setCurrentAudio(audio);
      setIsPlaying(true);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const filteredAudios = library.audios.filter(a => 
    (a.title || a.topic_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPdfs = library.pdfs.filter(p =>
    (p.title || p.filename || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50">
      {/* Header */}
      <nav className="border-b border-slate-200 bg-white/70 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Volver
            </Button>
            <div className="flex items-center gap-2">
              <OposaiLogo size={36} showText={false} />
              <h1 className="text-xl font-bold">Biblioteca</h1>
            </div>
            <Button
              onClick={downloadForOffline}
              disabled={downloadingOffline}
              variant="outline"
              className="rounded-full"
            >
              {downloadingOffline ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <><CloudOff className="w-4 h-4 mr-2" /> Descargar Offline</>
              )}
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm text-center">
            <Volume2 className="w-8 h-8 mx-auto mb-2 text-blue-500" />
            <p className="text-2xl font-bold text-slate-900">{library.audios.length}</p>
            <p className="text-sm text-slate-600">Audios</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm text-center">
            <FileText className="w-8 h-8 mx-auto mb-2 text-red-500" />
            <p className="text-2xl font-bold text-slate-900">{library.pdfs.length}</p>
            <p className="text-sm text-slate-600">Documentos</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm text-center">
            <CloudOff className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
            <p className="text-2xl font-bold text-slate-900">{offlineStats.audios}</p>
            <p className="text-sm text-slate-600">Offline</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar en la biblioteca..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="books" className="rounded-full">
              <BookOpen className="w-4 h-4 mr-2" /> Libros ({library.books?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="audios" className="rounded-full">
              <Volume2 className="w-4 h-4 mr-2" /> Audios ({filteredAudios.length})
            </TabsTrigger>
            <TabsTrigger value="pdfs" className="rounded-full">
              <FileText className="w-4 h-4 mr-2" /> Documentos ({filteredPdfs.length})
            </TabsTrigger>
          </TabsList>

          {/* Books Tab */}
          <TabsContent value="books">
            {library.books?.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border border-slate-200">
                <BookOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-600">No hay libros disponibles</p>
                <p className="text-sm text-slate-400 mt-1">Los libros generados aparecerán aquí</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {library.books?.map((book, idx) => (
                  <div 
                    key={book.book_id || idx}
                    className="bg-white rounded-xl border border-slate-200 p-5 hover:border-indigo-200 transition-all"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-20 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center text-white">
                        <BookOpen className="w-8 h-8" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-slate-900 text-lg">{book.title}</h3>
                        <p className="text-sm text-slate-500 mt-1">
                          {book.pages ? `${book.pages} páginas • ` : ''}
                          {book.premium ? '⭐ Premium' : 'Estándar'}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          Generado: {new Date(book.created_at).toLocaleDateString('es-ES')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {book.files?.pdf && (
                          <a
                            href={`${API}/books/download/${book.book_id}?format=pdf`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-colors"
                            onClick={(e) => {
                              e.preventDefault();
                              const token = localStorage.getItem('session_token');
                              window.open(`${API}/books/download/${book.book_id}?format=pdf&token=${token}`, '_blank');
                            }}
                          >
                            <Download className="w-4 h-4" />
                            Descargar PDF
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Audios Tab */}
          <TabsContent value="audios">
            {filteredAudios.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border border-slate-200">
                <Volume2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-600">No hay audios disponibles</p>
                <p className="text-sm text-slate-400 mt-1">Los audios generados aparecerán aquí</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredAudios.map((audio, idx) => (
                  <div 
                    key={audio.audio_id || idx}
                    className={`bg-white rounded-xl border p-4 flex items-center gap-4 transition-all ${
                      currentAudio?.audio_id === audio.audio_id ? 'border-blue-300 bg-blue-50' : 'border-slate-200 hover:border-blue-200'
                    }`}
                  >
                    <button
                      onClick={() => playAudio(audio)}
                      className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                        currentAudio?.audio_id === audio.audio_id && isPlaying
                          ? 'bg-blue-500 text-white'
                          : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                      }`}
                    >
                      {currentAudio?.audio_id === audio.audio_id && isPlaying ? (
                        <Pause className="w-5 h-5" />
                      ) : (
                        <Play className="w-5 h-5 ml-0.5" />
                      )}
                    </button>
                    
                    <div className="flex-1">
                      <h3 className="font-medium text-slate-900">
                        {audio.title || audio.topic_name || 'Audio sin título'}
                      </h3>
                      {audio.topic_name && audio.title !== audio.topic_name && (
                        <p className="text-sm text-slate-500">{audio.topic_name}</p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDuration(audio.duration_seconds)}
                      </span>
                      {audio.audio_url && (
                        <a 
                          href={audio.audio_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-600"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* PDFs Tab */}
          <TabsContent value="pdfs">
            {filteredPdfs.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border border-slate-200">
                <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-600">No hay documentos disponibles</p>
                <p className="text-sm text-slate-400 mt-1">Los documentos subidos aparecerán aquí</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredPdfs.map((pdf, idx) => (
                  <div 
                    key={pdf.pdf_id || pdf.material_id || idx}
                    className="bg-white rounded-xl border border-slate-200 p-4 hover:border-red-200 transition-all"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-6 h-6 text-red-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-slate-900 truncate">
                          {pdf.title || pdf.filename || 'Documento'}
                        </h3>
                        {pdf.description && (
                          <p className="text-sm text-slate-500 line-clamp-2">{pdf.description}</p>
                        )}
                        {pdf.topic_name && (
                          <p className="text-xs text-slate-400 mt-1">{pdf.topic_name}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                      <span className="text-xs text-slate-400">
                        {pdf.file_size ? `${(pdf.file_size / 1024 / 1024).toFixed(1)} MB` : ''}
                      </span>
                      {(pdf.file_url || pdf.url) && (
                        <a
                          href={pdf.file_url || pdf.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-sm text-red-500 hover:text-red-600"
                        >
                          <ExternalLink className="w-4 h-4" /> Abrir
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Current playing indicator */}
        {currentAudio && (
          <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full bg-white ${isPlaying ? 'animate-pulse' : ''}`} />
            <span className="font-medium">{currentAudio.title || currentAudio.topic_name}</span>
            <button onClick={() => playAudio(currentAudio)} className="ml-2">
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LibraryPage;
